import React from 'react';
import { Chip } from '@mui/material';

// Pure display helpers shared by the orders table, the details dialog and the calendar chips.
// Moved verbatim out of OrdersManagement.js (calendar + notes design, 2026-10-17).

export const TX_TYPE_COLORS = {
  CUSTOMER_AUTHORIZATION: 'default',
  CUSTOMER_CHARGE: 'success',
  CUSTOMER_REFUND: 'warning',
  VOID: 'error',
  PROVIDER_PAYOUT: 'info',
  PLATFORM_FEE: 'secondary',
};

export const TX_TYPE_KEYS = {
  CUSTOMER_AUTHORIZATION: 'orders.txAuthorization',
  CUSTOMER_CHARGE: 'orders.txCharge',
  CUSTOMER_REFUND: 'orders.txRefund',
  VOID: 'orders.txVoid',
  PROVIDER_PAYOUT: 'orders.txPayout',
  PLATFORM_FEE: 'orders.txPlatformFee',
};

export const getPaymentChip = (orderTxs, t) => {
  if (!orderTxs || orderTxs.length === 0) {
    return null;
  }
  // Sort by createdAt descending to find the latest
  const sorted = [...orderTxs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const latest = sorted[0];

  if (latest.type === 'CUSTOMER_REFUND' || latest.type === 'VOID') {
    return <Chip label={t('orders.payRefunded')} color="error" size="small" />;
  }
  if (orderTxs.some(tx => tx.type === 'CUSTOMER_CHARGE' && tx.status === 'COMPLETED')) {
    return <Chip label={t('orders.payPaid')} color="success" size="small" />;
  }
  if (orderTxs.some(tx => tx.type === 'CUSTOMER_AUTHORIZATION')) {
    return <Chip label={t('orders.payAuthorized')} color="warning" size="small" />;
  }
  return null;
};

export const getOrderTypeDisplay = (order, t) => {
  if (order.orderType === 'STORE') {
    return t('orders.storeOrder');
  }
  // design §A: before a proposal, dogServiceType is a PLACEHOLDER the server seeded because the
  // column is NOT NULL. Showing it in the list presents an arbitrary service as the customer's
  // requirement — the exact thing the inquiry redesign exists to stop.
  if (!order.hasProposal) return t('orders.serviceOrder');
  return order.dogServiceType
    ? t('services.type' + order.dogServiceType, { defaultValue: order.dogServiceType.replace(/_/g, ' ') })
    : t('orders.serviceOrder');
};

/**
 * The payable amount of a SERVICE order. `acceptedQuoteAmount` is the authoritative figure
 * (contract §6): it is what the provider proposed and the customer accepted. The legacy
 * `price` double is only populated by the pre-redesign flow and reads 0 on every quoted order,
 * which is what showed "₪0.00" on orders that had a real price.
 */
export const serviceOrderAmount = (order) => {
  if (order.acceptedQuoteAmount != null) return Number(order.acceptedQuoteAmount);
  return order.price || 0;
};

export const calculateOrderTotal = (order) => {
  if (order.orderType === 'STORE') {
    return order.calculatedTotal || order.orderItems?.reduce((sum, item) => sum + (item.totalPrice || 0), 0) || 0;
  }
  return serviceOrderAmount(order);
};

export const getOrderDetails = (order, t) => {
  if (order.orderType === 'STORE') {
    return {
      type: t('orders.storeOrder'),
      items: order.orderItems || [],
      total: calculateOrderTotal(order),
      itemCount: order.orderItems?.length || 0,
    };
  }
  return {
    type: t('orders.serviceOrder'),
    service: order.providerDogService || { dogServiceType: order.dogServiceType, price: serviceOrderAmount(order) },
    total: serviceOrderAmount(order),
    duration: order.numOfDays,
  };
};
